import React from "react";
import { LatestResultProps, Team, Scores } from "./types";
import { formatDate, omitNation } from "../../../helpers/utils";
import "./index.scss";
import { Carousel } from "../../Carousel/Carousel";
import ReactCSSTransitionGroup from "react-addons-css-transition-group";

interface Results {
  results: LatestResultProps[];
  seeAllLink?: string;
  seeAllText?: string;
  page: number;
  itemsPerPage: number; // e.g. 3 on desktop, 2 on tablet, 1 on mobile
  pagerPadding?: boolean;
}

const LatestResultList = (props: Results) => {
  const { results, page, itemsPerPage } = props;
  const slicedResults = results.slice(
    page * itemsPerPage,
    (page + 1) * itemsPerPage
  );

  return (
    <>
      <ReactCSSTransitionGroup
        transitionName="carousel__animation"
        transitionEnterTimeout={500}
        transitionLeaveTimeout={500}
        className="carousel__animation-container"
      >
        {slicedResults.map(item => {
          const { isInDispute, isUnverified } = item;
          return (
            <div
              key={item.matchId}
              className={
                "latest-result-widget " +
                (isInDispute ? "latest-result-widget--is-in-dispute" : "") +
                (isUnverified ? "latest-result-widget--is-unverified" : "")
              }
            >
              <div className="latest-result-widget__inner">
                <header>
                  <div>
                    <h3 className="latest-result-widget__tourn-name">
                      <a href={item.tournamentLink}>{item.tournamentName}</a>
                    </h3>
                    <p className="latest-result-widget__tourn-dates">
                      {formatDate(item.startDate).shortNoYear} to{" "}
                      {formatDate(item.endDate).shortNoComma}
                    </p>
                  </div>
                  <a className="more-info" href={item.moreInfoLink}>
                    More info
                  </a>
                </header>
                {item.teams[0].players[1] === null ? (
                  <a
                    className="latest-result-widget__h-to-h"
                    href={item.h2hLink}
                  >
                    H2H
                  </a>
                ) : null}
                {item.teams.map((teamDetails: Team, key: number) => {
                  return (
                    <div
                      key={key}
                      className={
                        "latest-result-widget__team-info " +
                        `latest-result-widget__team-info--team-${key + 1} ` +
                        (teamDetails.isWinner ? "is-winner" : "")
                      }
                    >
                      <div className="latest-result-widget__teams">
                        {teamDetails.players[1] === null ? (
                          <div className="latest-result-widget__team-singles">
                            <span className="latest-result-widget__nationality">
                              <span
                                className={`itf-flags itf-flags--${
                                  teamDetails.players[0]!.nationality
                                  }`}
                              />
                              {omitNation(teamDetails.players[0]!.nationality)}
                            </span>
                            <div className="player-wrapper">
                              <a href={teamDetails.players[0]!.profileLink}>
                                <span className="latest-result-widget__first-name">
                                  {teamDetails.players[0]!.givenName}
                                </span>
                                <span className="latest-result-widget__last-name">
                                  {teamDetails.players[0]!.familyName}
                                </span>
                              </a>
                            </div>
                          </div>
                        ) : (
                            <div className="latest-result-widget__team-doubles">
                              {teamDetails.players.map((player, key) => {
                                return (
                                  <div
                                    className="latest-result-widget__team-doubles-player"
                                    key={key}
                                  >
                                    <span className="latest-result-widget__nationality">
                                      <span
                                        className={`itf-flags itf-flags--${
                                          player!.nationality
                                          }`}
                                      />
                                      {omitNation(player!.nationality)}
                                    </span>
                                    <a href={player!.profileLink}>
                                      <span className="latest-result-widget__first-name">
                                        {player!.givenName}
                                      </span>
                                      <span className="latest-result-widget__last-name">
                                        {player!.familyName}
                                      </span>
                                    </a>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        <span className="latest-result-widget__seeding">
                          {teamDetails.seeding
                            ? `[${teamDetails.seeding}]`
                            : null}
                        </span>
                        {teamDetails.isWinner ? (
                          <span className="latest-result-widget__winner-tick" />
                        ) : null}
                      </div>
                      <div className="latest-result-widget__scores">
                        {teamDetails.scores.map(
                          (score: null | Scores, key: number) => {
                            return score !== null ? (
                              <span
                                key={key}
                                className="latest-result-widget__score"
                              >
                                {score.losingScore === null ? (
                                  score.score
                                ) : (
                                    <>
                                      6
                                    <span className="losing-score">
                                        {score.losingScore}
                                      </span>
                                    </>
                                  )}
                              </span>
                            ) : null;
                          }
                        )}
                        {isInDispute
                          ? [0, 1, 2].map(value => {
                            return (
                              <span
                                key={value}
                                className="latest-result-widget__score latest-result-widget__score--in-dispute"
                              />
                            );
                          })
                          : null}
                      </div>
                    </div>
                  );
                })}
                {isInDispute ? (
                  <div className="in-dispute-alert">
                    <span>In Dispute</span>
                  </div>
                ) : null}
                {isUnverified ? (
                  <div className="unverified-alert">
                    <span>Unverified</span>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </ReactCSSTransitionGroup>
    </>
  );
};

const LatestResults = (props: Results) => {
  const itemsToDisplay = { DESKTOPXL: 3, DESKTOP: 3, TABLET: 2, MOBILE: 1 };
  return (
    <>
      <h2>Latest Results</h2>
      <Carousel
        totalItems={props.results.length}
        itemsToDisplay={itemsToDisplay}
        pagerPadding={props.pagerPadding}
      >
        <LatestResultList
          results={props.results}
          page={0}
          itemsPerPage={itemsToDisplay["DESKTOP"]}
        />
      </Carousel>
      {props.seeAllLink ? (
        <div className="latest-results-link center-content">
          <a href={props.seeAllLink} className="btn">
            {props.seeAllText}
          </a>
        </div>
      ) : null}
    </>
  );
};

export default LatestResults;
